import { defineStyle, useColorMode } from '@chakra-ui/react';
import React, { useMemo } from 'react';

import { Typography } from '~/packages/common-frontend/components/Typography';
import { ColorScheme, Sizes } from '~/packages/common-frontend/theme';
import {
  BORDER_RADII,
  COLOR_SETS,
  FONT_WEIGHTS,
  SPACING,
  TEXT_STYLES,
} from '~/packages/common-frontend/theme/tokens';
import {
  createResolveColor,
  size as valueBySizeProp,
} from '~/packages/common-frontend/theme/utils';

// Exports
type CounterProps = {
  count: number;
  maxCount?: number;
  size?: Sizes;
  colorScheme?: ColorScheme;
  increaseContrast?: boolean;
  TypographyProps?: React.ComponentProps<typeof Typography>;
};
export const Counter = ({
  count,
  maxCount = count,
  colorScheme = 'gray',
  size = 'md',
  increaseContrast = false,
  TypographyProps,
}: CounterProps) => {
  const { colorMode } = useColorMode();
  const resolveColor = createResolveColor({ colorMode, colorScheme });

  const textStyleProps = useMemo(
    () =>
      defineStyle({
        alignSelf: 'center',
        fontWeight: FONT_WEIGHTS.semibold,
        fontSize: valueBySizeProp({
          sm: TEXT_STYLES.xs.fontSize,
          md: TEXT_STYLES.sm.fontSize,
          lg: TEXT_STYLES.md.fontSize,
        })({ size }),
        lineHeight: valueBySizeProp({
          sm: TEXT_STYLES.xs.lineHeight,
          md: TEXT_STYLES.sm.lineHeight,
          lg: TEXT_STYLES.md.lineHeight,
        })({ size }),
        paddingBlock: 0,
        paddingInline: valueBySizeProp({
          sm: SPACING[1],
          md: SPACING[1],
          lg: SPACING[1.5],
        })({ size }),
        borderRadius: valueBySizeProp({
          sm: BORDER_RADII.base,
          md: BORDER_RADII.md,
          lg: BORDER_RADII.lg,
        })({ size }),
        backgroundColor: increaseContrast
          ? resolveColor(COLOR_SETS.muted[1])
          : resolveColor(COLOR_SETS.muted[0]),
      }),
    [increaseContrast, resolveColor, size],
  );
  return (
    <Typography
      inline
      colorScheme={colorScheme}
      variant="detail"
      {...TypographyProps}
      TextProps={{
        sx: textStyleProps,
        ...TypographyProps?.TextProps,
      }}
    >
      {count <= maxCount ? count : `${maxCount}+`}
    </Typography>
  );
};
